import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Loader2, Check, Sparkles, ArrowRight, ArrowLeft, Home } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { SEO } from "@/components/shared/SEO";
import { BrandMark } from "@/components/shared/BrandMark";
import TrialRibbon from "@/components/marketing/TrialRibbon";
import { PlanCalculator } from "@/components/pricing/PlanCalculator";

interface Plan {
  id: "free" | "solo" | "starter" | "pro" | "business";
  name: string;
  tagline: string;
  highlight: boolean;
  features: {
    accounts: number;
    comments_per_month: number;
    retention_days: number;
    ai_replies: boolean;
    bulk_actions: boolean;
    email_digests: boolean;
    priority_support: boolean;
    intent_radar?: boolean;
    shopify_connect?: boolean;
  };
  monthly_display: string;
  yearly_display: string;
  yearly_savings: string;
  monthly_amount_minor: number;
  yearly_amount_minor: number;
}

interface PlansData {
  currency: string;                 // display currency (USD / INR / EUR / …)
  charge_currency: "USD" | "INR";   // what actually gets billed
  provider: "stripe" | "razorpay";
  stripe_configured: boolean;
  razorpay_configured: boolean;
  razorpay_key_id: string;
  plans: Plan[];
}

// The 12 supported display currencies — kept in sync with backend
// `services/fx.py::SUPPORTED_CURRENCIES`. Symbol + native name shown in
// the picker so a Brazilian visitor sees "R$ · Brazilian Real".
const CURRENCY_OPTIONS: { code: string; symbol: string; name: string }[] = [
  { code: "USD", symbol: "$",    name: "US Dollar" },
  { code: "INR", symbol: "₹",    name: "Indian Rupee" },
  { code: "EUR", symbol: "€",    name: "Euro" },
  { code: "GBP", symbol: "£",    name: "British Pound" },
  { code: "AUD", symbol: "A$",   name: "Australian Dollar" },
  { code: "CAD", symbol: "C$",   name: "Canadian Dollar" },
  { code: "SGD", symbol: "S$",   name: "Singapore Dollar" },
  { code: "AED", symbol: "د.إ",  name: "UAE Dirham" },
  { code: "JPY", symbol: "¥",    name: "Japanese Yen" },
  { code: "ZAR", symbol: "R",    name: "South African Rand" },
  { code: "BRL", symbol: "R$",   name: "Brazilian Real" },
  { code: "MXN", symbol: "Mex$", name: "Mexican Peso" },
];

const featLine = (n: number, unit: string) => (n === 0 ? `Unlimited ${unit}` : `${n.toLocaleString()} ${unit}`);

const PricingPage: React.FC = () => {
  // Default to MONTHLY (Feb 2026 rebuild) — pricing is now market-lowest, so
  // the monthly sticker price is the star of the show. Users can flip to
  // yearly for the 2-months-free discount.
  const [cycle, setCycle] = useState<"monthly" | "yearly">("monthly");
  const [busy, setBusy] = useState<string | null>(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();

  // Currency override — persists in localStorage so the choice survives navigation.
  // Any of the 12 supported display currencies is valid; the backend derives
  // the actual charge currency (USD / INR) from the display code.
  const [currencyOverride, setCurrencyOverride] = useState<string | null>(
    () => (typeof window !== "undefined"
      ? localStorage.getItem("sb_pricing_currency")
      : null),
  );

  useEffect(() => {
    if (sp.get("canceled")) {
      const t = setTimeout(() => toast.info("Checkout canceled — nothing was charged."), 0);
      return () => clearTimeout(t);
    }
  }, [sp]);

  const q = useQuery<PlansData>({
    queryKey: ["billing-plans", currencyOverride],
    queryFn: async () => (await api.get("/billing/plans", {
      params: currencyOverride ? { currency: currencyOverride } : undefined,
    })).data,
  });

  const flipCurrency = (c: string) => {
    setCurrencyOverride(c);
    try { localStorage.setItem("sb_pricing_currency", c); } catch {}
  };

  const startCheckout = useMutation({
    mutationFn: async (plan_id: "solo" | "starter" | "pro" | "business") => {
      const { data } = await api.post("/billing/checkout", {
        plan_id, cycle, origin_url: window.location.origin,
      });
      return data as { url: string; session_id: string; provider: string; currency: string; amount_minor: number };
    },
    onSuccess: async (d, plan_id) => {
      // Razorpay: only fires when the effective charge_currency is INR
      if (d.provider === "stripe") {
        window.location.href = d.url; // redirect to Stripe Checkout
      } else {
        // Razorpay: open modal in-page
        if (!q.data) return;
        setBusy(plan_id);
        try {
          const paid = await openRazorpayCheckout({
            order_id: d.session_id,
            amount: d.amount_minor,
            currency: "INR",
            key: q.data.razorpay_key_id || "",
            name: "SociBaba",
            description: `${plan_id} · ${cycle}`,
            prefill: { email: user?.email, name: user?.name },
          });
          if (paid) {
            toast.success("Payment received — activating your plan…");
            // poll status once to be safe (webhook usually beats us to it)
            await api.get(`/billing/status/${d.session_id}`);
            nav("/settings/billing");
          } else {
            toast.info("Payment canceled — you can try again anytime.");
          }
        } finally {
          setBusy(null);
        }
      }
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    },
  });

  const cta = (p: Plan) => {
    if (p.id === "free") return null;
    const running = startCheckout.isPending || busy === p.id;
    const handleClick = () => {
      if (!user) {
        nav(`/signup?next=/pricing&plan=${p.id}`);
        return;
      }
      startCheckout.mutate(p.id as "solo" | "starter" | "pro" | "business");
    };
    return (
      <Button
        data-testid={`pricing-checkout-${p.id}`}
        disabled={running}
        onClick={handleClick}
        className={
          p.highlight
            ? "w-full bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white"
            : "w-full bg-white border border-[color:var(--sg-fg)] text-[color:var(--sg-fg)] hover:bg-slate-50"
        }
      >
        {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1.5" />}
        {user ? `Start ${p.name}` : `Sign up · ${p.name}`}
      </Button>
    );
  };

  return (
    <>
      <SEO title="Pricing · SociBaba" description="Turn every comment into a lead, a fix, or a decision. Simple, transparent pricing." path="/pricing" />
      <TrialRibbon surface="pricing" />
      <div className="min-h-screen overflow-x-hidden bg-[color:var(--sg-bg,white)]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="pricing-nav-actions">
          <Link to="/" data-testid="pricing-logo" className="flex items-center gap-2.5 group">
            <BrandMark
              showIcon
              iconClassName="h-8 w-8 mr-2 inline-block align-middle dark:invert group-hover:scale-105 transition-transform"
              className="font-serif-display text-lg font-bold tracking-tight text-[color:var(--sg-fg)] flex items-center"
            />
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="pricing-back-btn"
              onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
              className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--sg-border)] bg-white hover:bg-[color:var(--sg-muted-bg)] text-[color:var(--sg-fg)] text-sm font-medium px-3.5 py-1.5 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <Link
              to="/"
              data-testid="pricing-home-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--sg-border)] bg-white hover:bg-[color:var(--sg-muted-bg)] text-[color:var(--sg-fg)] text-sm font-medium px-3.5 py-1.5 transition-colors"
            >
              <Home className="h-4 w-4" />
              Home
            </Link>
          </div>
        </div>
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 sg-chip bg-violet-50 text-violet-700 border-violet-200 mb-3">
            <Sparkles className="h-3 w-3" /> 14-day Pro trial, no card required
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            Simple pricing.<br />Signal → Action.
          </h1>
          <p className="text-[color:var(--sg-muted)] mt-4 max-w-xl mx-auto">
            Every plan syncs comments, classifies intent, drafts replies, and tracks leads.
            Pick a size — grow into the next tier as your community grows.
          </p>

          {/* Shoppers-are-free banner — Feb 2026: SociBaba's shopping surfaces
              (`/shop`, `/cart`, `/founder-story`) are 100% free forever.
              The plans below exist for brands, creators, sellers, and agencies
              who want to *use* the dashboard tools. Buyers should never feel
              a paywall — reassure them right at the top of the page. */}
          <div
            data-testid="pricing-buyers-free-banner"
            className="mt-5 mx-auto max-w-3xl rounded-xl border border-sky-200 bg-gradient-to-r from-sky-50 via-white to-sky-50 dark:border-sky-500/30 dark:from-sky-500/10 dark:via-transparent dark:to-sky-500/10 px-5 py-3 flex items-center justify-center gap-3 flex-wrap"
          >
            <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-sky-700 dark:text-sky-300 bg-white/70 dark:bg-slate-900/60 border border-sky-200 dark:border-sky-500/40 rounded-full px-2 py-0.5">
              <Sparkles className="h-3 w-3" /> Shoppers = Free
            </span>
            <span className="text-sm text-sky-900 dark:text-sky-100">
              Shopping, browsing, SibaScore, and shared carts are <b>free forever</b> for individuals & guest buyers.
              <span className="text-sky-700 dark:text-sky-300"> The plans below are for brands, creators, sellers & agencies.</span>
            </span>
          </div>

          {/* Free the Core banner — Feb 2026: SociBaba's flagship surfaces
              ship on every plan, including Free. */}
          <div
            data-testid="free-the-core-banner"
            className="mt-4 mx-auto max-w-2xl rounded-xl border-2 border-dashed border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 px-5 py-3 flex items-center justify-center gap-3 flex-wrap"
          >
            <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-emerald-700 bg-white border border-emerald-200 rounded-full px-2 py-0.5">
              <Sparkles className="h-3 w-3" /> Free the Core
            </span>
            <span className="text-sm text-emerald-900">
              <b>Intent Radar</b> + <b>Shopify Connect</b> ship on the Free plan.
              <span className="text-emerald-700"> Feel the wow before the paywall.</span>
            </span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3 mb-8 flex-wrap">
          <div className="flex items-center gap-1 p-1 border border-[color:var(--sg-border)] rounded-full bg-white">
            {(["monthly", "yearly"] as const).map((c) => (
              <button
                key={c}
                data-testid={`pricing-cycle-${c}`}
                onClick={() => setCycle(c)}
                className={`px-5 py-1.5 text-sm rounded-full transition ${
                  cycle === c
                    ? "bg-[color:var(--sg-fg)] text-white font-semibold"
                    : "text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]"
                }`}
              >
                {c === "monthly" ? "Monthly" : "Yearly · save 20%"}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 p-1 border border-[color:var(--sg-border)] rounded-full bg-white" data-testid="pricing-currency-toggle">
            {/* Native <select> — 12 currencies. Keyboard-native, screen-reader
                native, opens on tap/click. Saffron border echoes the active
                state on the older two-button toggle. */}
            <label className="sr-only" htmlFor="pricing-currency-select">Display currency</label>
            <select
              id="pricing-currency-select"
              data-testid="pricing-currency-select"
              value={q.data?.currency || currencyOverride || "USD"}
              onChange={(e) => flipCurrency(e.target.value)}
              className="px-3 py-1.5 text-sm rounded-full font-medium bg-[color:var(--sb-saffron)] text-white border-0 focus:outline-none focus:ring-2 focus:ring-[color:var(--sb-saffron)]/40 cursor-pointer"
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c.code} value={c.code} className="text-slate-900 bg-white">
                  {c.symbol} {c.code} · {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {q.isLoading ? (
          <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[color:var(--sg-muted)]" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="pricing-grid">
            {q.data?.plans.map((p) => {
              const price = cycle === "monthly" ? p.monthly_display : p.yearly_display;
              const suffix = cycle === "monthly" ? "/mo" : "/yr";
              return (
                <div
                  key={p.id}
                  data-testid={`plan-card-${p.id}`}
                  className={`sg-card p-6 flex flex-col ${p.highlight ? "border-[color:var(--sg-fg)] ring-2 ring-[color:var(--sg-fg)]" : ""}`}
                >
                  {p.highlight && (
                    <div className="text-[10px] uppercase tracking-widest bg-[color:var(--sg-fg)] text-white px-2 py-0.5 rounded-full w-fit mb-3">
                      Recommended
                    </div>
                  )}
                  <div className="font-display text-xl font-bold tracking-tight">{p.name}</div>
                  <div className="text-xs text-[color:var(--sg-muted)] mt-1 leading-snug min-h-[36px]">
                    {p.tagline}
                  </div>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="font-display text-3xl font-bold tracking-tight">{price}</span>
                    <span className="text-xs text-[color:var(--sg-muted)]">{p.id === "free" ? "" : suffix}</span>
                  </div>
                  {cycle === "yearly" && p.yearly_savings && (
                    <div className="text-[11px] text-emerald-700 mt-1">{p.yearly_savings}</div>
                  )}
                  <ul className="mt-5 space-y-2 text-xs text-[color:var(--sg-fg)] flex-1">
                    {p.features.intent_radar && (
                      <li className="flex items-start gap-1.5" data-testid={`feat-intent-radar-${p.id}`}>
                        <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                        <span>
                          <b>Intent Radar</b>
                          {p.id === "free" && (
                            <span className="ml-1 inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold px-1.5 py-0.5 align-middle">
                              FREE
                            </span>
                          )}
                        </span>
                      </li>
                    )}
                    {p.features.shopify_connect && (
                      <li className="flex items-start gap-1.5" data-testid={`feat-shopify-connect-${p.id}`}>
                        <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                        <span>
                          <b>Shopify Connect</b>
                          {p.id === "free" && (
                            <span className="ml-1 inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold px-1.5 py-0.5 align-middle">
                              FREE
                            </span>
                          )}
                        </span>
                      </li>
                    )}
                    <li className="flex items-start gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> {featLine(p.features.accounts, "connected accounts")}</li>
                    <li className="flex items-start gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> {featLine(p.features.comments_per_month, "comments/mo")}</li>
                    <li className="flex items-start gap-1.5"><Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> {p.features.retention_days === 730 ? "2y" : (p.features.retention_days >= 365 ? "1y" : `${p.features.retention_days}d`)} data retention</li>
                    <li className={`flex items-start gap-1.5 ${p.features.ai_replies ? "" : "opacity-40"}`}><Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> AI reply drafts</li>
                    <li className={`flex items-start gap-1.5 ${p.features.bulk_actions ? "" : "opacity-40"}`}><Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> Bulk comment actions</li>
                    <li className={`flex items-start gap-1.5 ${p.features.email_digests ? "" : "opacity-40"}`}><Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> Complaint email digests</li>
                    <li className={`flex items-start gap-1.5 ${p.features.priority_support ? "" : "opacity-40"}`}><Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> Priority support</li>
                  </ul>
                  <div className="mt-5">{cta(p)}</div>
                </div>
              );
            })}
          </div>
        )}
        {q.data && (
          <div className="text-center text-xs text-[color:var(--sg-muted)] mt-6" data-testid="pricing-disclaimer">
            Prices shown in <b>{q.data.currency}</b>.
            {q.data.currency !== q.data.charge_currency && (
              <> Charged in <b>{q.data.charge_currency}</b> via <b className="capitalize">{q.data.provider}</b> —
              your card statement will show the <b>{q.data.charge_currency}</b> amount at your bank's FX rate.</>
            )}
            {q.data.currency === q.data.charge_currency && (
              <> Billing via <b className="capitalize">{q.data.provider}</b>.</>
            )}
            {" "}Change your preference in <a className="underline" href="/settings">Settings</a>.
            Cancel anytime — access continues to the end of the current period.
          </div>
        )}
        {!q.data && !user && (
          <div className="text-center text-xs text-[color:var(--sg-muted)] mt-6">
            <Link to="/signup" className="underline">Sign up</Link> to see prices in your local currency. Cancel anytime.
          </div>
        )}

        {/* ---- SociBaba Custom Plan Calculator (Pillar 2) ---- */}
        <div className="mt-16 pt-12 border-t border-neutral-200">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 sg-chip bg-orange-50 text-orange-700 border-orange-200 mb-3">
              <Sparkles className="h-3 w-3" /> Custom plan · pick every number
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Need something between the tiers?
            </h2>
            <p className="text-[color:var(--sg-muted)] mt-3 max-w-xl mx-auto text-sm">
              Slide every knob until it matches your business — AI credits,
              SKUs, social channels, team seats, marketing sends, and countries.
              We&apos;ll build a bespoke quote or route you to Enterprise.
            </p>
          </div>
          <PlanCalculator />
        </div>
      </div>
      </div>
    </>
  );
};

export default PricingPage;
